import React from 'react';
import { Link } from 'react-router-dom';
import { useGScoreDashboard } from '../../hooks/useGScoreDashboard';

// A null accuracy or reaction time means too few scored trials to be
// meaningful — never 0. Rendering 0% or 0ms would read as "you got nothing
// right", which is a worse lie than an honest dash.
const formatAccuracy = (accuracy) => (accuracy == null ? '—' : `${Math.round(accuracy * 100)}%`);
const formatReactionTime = (ms) => (ms == null ? '— ms' : `${Math.round(ms)} ms`);

// Rendered as one string, not several interpolated JSX children, so it is
// exactly one text node — a test asserting the literal count copy is not
// tripped up by React splitting "3", " block", "s", " · " etc. into separate
// sibling text nodes.
const trialCountLabel = (blocksCompleted, scoredTrials) =>
  `${blocksCompleted} block${blocksCompleted === 1 ? '' : 's'} · ${scoredTrials} trial${
    scoredTrials === 1 ? '' : 's'
  }`;

// The level moved; say which way rather than leaving the learner to infer it
// from two numbers. A session that ended easier than it started is not a
// failure and must not be hidden or euphemized.
function levelChangeLabel(levelDelta) {
  if (levelDelta > 0) return 'This one got harder.';
  if (levelDelta < 0) return 'This one got easier.';
  return 'Held steady this time.';
}

// Leads with level and streak — progression, not measurement. Per-block
// accuracy appears only as a rail: a single large percentage reads as a
// cognitive score, and training must never show one.
export default function TrainingResults({ blockStats = [], summary = null }) {
  const levels = blockStats.map((s) => s.nextLevel).filter((n) => n != null);
  const finalLevel = summary ? summary.exitLevel : (levels.length ? levels[levels.length - 1] : null);

  // Fetch dashboard to get the freshly-updated streak. A failed or loading
  // streak fetch must never block the results screen — the session already
  // succeeded and its data is already saved.
  const { data: dashData, loading: dashLoading } = useGScoreDashboard();
  const currentStreak = dashData?.streak?.currentDays ?? null;
  const showStreak = !dashLoading && currentStreak != null && currentStreak > 0;
  const streakLabel = currentStreak === 1 ? '1 day streak' : `${currentStreak} day streak`;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'var(--bg-primary)',
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--space-5)',
        overflowY: 'auto',
      }}
    >
      <div style={{ maxWidth: 420, textAlign: 'center' }}>
        <div
          style={{
            fontSize: 'var(--text-numeric-size)',
            fontWeight: 700,
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: 'var(--text-label-tracking)',
            marginBottom: 'var(--space-4)',
          }}
        >
          Session complete
        </div>

        {finalLevel != null && (
          <div style={{ marginBottom: showStreak ? 'var(--space-2)' : 'var(--space-5)' }}>
            {/* "Difficulty reached", not "Reached" alone — attainment framing
                ("Reached Level 4") reads as a rank a learner earned. Naming the
                difficulty dial explicitly keeps this the same sentence as
                levelChangeLabel below: a setting the engine adjusted, not a
                verdict on the learner. Sized well below ScoreHero's composite
                (64px) on purpose, so the two never share a silhouette. */}
            <div style={{ fontSize: 'var(--text-body-size)', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)' }}>
              Difficulty reached
            </div>
            <div
              style={{
                fontSize: 32,
                fontWeight: 800,
                color: 'var(--text-primary)',
                letterSpacing: '-0.03em',
                lineHeight: 1,
              }}
            >
              Level {finalLevel}
            </div>
          </div>
        )}

        {showStreak && (
          <div
            style={{
              fontSize: 'var(--text-body-size)',
              color: 'var(--text-secondary)',
              marginBottom: 'var(--space-5)',
            }}
          >
            {streakLabel}
          </div>
        )}

        {/* Siblings, not a ternary: a real session reports both a
            sessionSummary AND per-block stats, and hiding the block rail the
            moment a summary exists would delete real detail with no code
            change to this rail's own branch and no test to catch it. */}
        {summary && (
          <div style={{ marginBottom: 'var(--space-5)' }}>
            <div style={{ fontSize: 'var(--text-body-size)', color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>
              {levelChangeLabel(summary.levelDelta)}
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'center' }}>
              <div
                style={{
                  flex: 1,
                  maxWidth: 140,
                  background: 'var(--glass-bg-medium)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3) var(--space-2)',
                }}
              >
                <div style={{ fontSize: 'var(--text-section-size)', fontWeight: 800, color: 'var(--text-primary)' }}>
                  {formatAccuracy(summary.accuracy)}
                </div>
                <div style={{ fontSize: 'var(--text-label-size)', color: 'var(--text-tertiary)', marginTop: 2 }}>
                  Accuracy
                </div>
              </div>
              <div
                style={{
                  flex: 1,
                  maxWidth: 140,
                  background: 'var(--glass-bg-medium)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3) var(--space-2)',
                }}
              >
                <div style={{ fontSize: 'var(--text-section-size)', fontWeight: 800, color: 'var(--text-primary)' }}>
                  {formatReactionTime(summary.meanReactionTimeMs)}
                </div>
                <div style={{ fontSize: 'var(--text-label-size)', color: 'var(--text-tertiary)', marginTop: 2 }}>
                  Reaction time
                </div>
              </div>
            </div>
            {summary.blocksCompleted != null && summary.scoredTrials != null && (
              <div style={{ fontSize: 'var(--text-label-size)', color: 'var(--text-tertiary)', marginTop: 'var(--space-3)' }}>
                {trialCountLabel(summary.blocksCompleted, summary.scoredTrials)}
              </div>
            )}
          </div>
        )}

        {blockStats.length > 0 && (
          <div style={{ marginBottom: 'var(--space-5)' }}>
            <div
              style={{
                fontSize: 'var(--text-label-size)',
                fontWeight: 700,
                color: 'var(--text-tertiary)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginBottom: 'var(--space-2)',
              }}
            >
              Blocks
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'center', alignItems: 'flex-end' }}>
              {blockStats.map((s, i) => (
                <div
                  key={i}
                  title={s.accuracy == null ? '—' : `${Math.round(s.accuracy * 100)}%`}
                  style={{
                    width: 26,
                    // accuracy is null when a block had too little scorable data.
                    // Render it as a stub, never as a zero-height bar.
                    height: s.accuracy == null ? 6 : Math.max(6, Math.round(s.accuracy * 44)),
                    borderRadius: 'var(--radius-sm)',
                    background:
                      s.accuracy == null ? 'var(--border-subtle)' : 'var(--text-primary)',
                  }}
                />
              ))}
            </div>
          </div>
        )}

        <Link
          to="/g-score"
          style={{
            display: 'inline-block',
            padding: 'var(--space-3) var(--space-5)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--btn-primary-bg)',
            color: 'var(--btn-primary-text)',
            fontSize: 'var(--text-body-size)',
            fontWeight: 700,
            textDecoration: 'none',
          }}
        >
          Done
        </Link>

        <p
          style={{
            margin: 'var(--space-5) auto 0',
            maxWidth: 320,
            fontSize: 'var(--text-numeric-size)',
            color: 'var(--text-tertiary)',
            lineHeight: 1.6,
          }}
        >
          Training builds the habit. Only assessments measure your progress.
        </p>
      </div>
    </div>
  );
}
